import {
    Alert,
    Box,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    TextField,
    Typography
} from '@mui/material';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import React, { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import styled from 'styled-components';
import { getAllIncomeTypes } from '../../../../data/api/incomes/IncomeRoutes';
import useIndex from '../../../../hooks/useIndex';
import moneyFormat from '../../../../hooks/moneyFormat';
import validErrors from '../../../../hooks/validErrors';

const PRICE_PER_MOVE = 1000;

/**
 * Props compartidos para los inputs numéricos del wizard:
 *  - `onWheel`: blurea el input cuando se hace scroll para que la rueda
 *    del mouse NO modifique el valor (default browser behavior molesto
 *    cuando el usuario está scrolleando el wizard y pasa por encima).
 *  - `onKeyDown`: bloquea los caracteres "-", "+", "e", "E" para que no
 *    se puedan tipear (los inputs HTML number los aceptan por defecto).
 *  - `inputProps.min`: piso 0/1 según el campo, refuerzo en runtime.
 */
const numberFieldProps = (extra = {}) => ({
    type: 'number',
    onWheel: (e) => e.target.blur(),
    onKeyDown: (e) => {
        if (['-', '+', 'e', 'E'].includes(e.key)) e.preventDefault();
    },
    inputProps: { min: 0, step: 1, ...extra },
});

const fieldStyles = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '14px',
        background: '#fff',
        color: '#10285f',
        fontWeight: 700,

        '& fieldset': {
            borderColor: 'rgba(15, 34, 77, 0.16)',
        },

        '&:hover fieldset': {
            borderColor: 'rgba(214, 170, 58, 0.7)',
        },

        '&.Mui-focused fieldset': {
            borderColor: '#d6aa3a',
            borderWidth: '2px',
        },
    },
};

const Step2Commercial = ({ control, register, watch, setValue, errors, requiresMovesDefault = true }) => {

    const { token } = useIndex();

    const [incomeTypes, setIncomeTypes] = useState([]);
    const [requiresMoves, setRequiresMoves] = useState(requiresMovesDefault);

    const moves = watch('moves_amount');
    const amount = watch('agreement_amount');
    const gift = watch('gift_moves');

    useEffect(() => {
        loadPaymentMethods();
    }, []);

    useEffect(() => {
        setRequiresMoves(requiresMovesDefault);
    }, [requiresMovesDefault]);

    const loadPaymentMethods = async () => {
        try {
            const { data } = await getAllIncomeTypes(token);
            if (data?.success) setIncomeTypes(data?.data ?? []);
        } catch (error) {
            validErrors(error);
        }
    };

    const handleMovesChange = (val) => {
        const m = parseInt(val);
        if (!Number.isNaN(m) && m >= 0) {
            setValue('moves_amount', m, { shouldValidate: true });
            setValue('agreement_amount', m * PRICE_PER_MOVE, { shouldValidate: true });
        }
    };

    const handleAmountChange = (val) => {
        const a = parseInt(val);
        if (!Number.isNaN(a) && a >= 0) {
            setValue('agreement_amount', a, { shouldValidate: true });
            setValue('moves_amount', Math.floor(a / PRICE_PER_MOVE), { shouldValidate: true });
        }
    };

    const handleRequiresMovesToggle = (e) => {
        const next = e.target.checked;
        setRequiresMoves(next);

        if (!next) {
            setValue('moves_amount', null);
            setValue('agreement_amount', null);
            setValue('gift_moves', null);
            setValue('income_types_id', null);
        }
    };

    // const giftMax = Number(moves || 0) > 0 ? Math.floor(Number(moves) / 1) : 0;
    // const giftExceeds = Number(gift || 0) > giftMax;

    return (
        <Wrapper>

            <Header>
                <Typography className="title">
                    Términos comerciales
                </Typography>

                <Typography className="subtitle">
                    Define valores, jugadas, pagos y condiciones del contrato
                </Typography>
            </Header>

            <SectionCard>
                <SectionTitle>
                    <AttachMoneyOutlinedIcon />
                    <Typography>Configuración comercial</Typography>
                </SectionTitle>

                <FormControlLabel
                    control={
                        <Switch
                            checked={requiresMoves}
                            onChange={handleRequiresMovesToggle}
                        />
                    }
                    label="Este contrato entrega jugadas a la empresa"
                />

                <Grid container spacing={2} mt={1}>
                    <Grid item xs={12} sm={requiresMoves ? 6 : 12}>
                        <TextField
                            fullWidth size="small" label="Tipo de contrato"
                            defaultValue="Contrato de Venta de Productos Digitales"
                            error={!!errors.agreement_type}
                            helperText={errors.agreement_type?.message}
                            {...register('agreement_type', { required: 'Obligatorio.' })}
                            sx={fieldStyles}
                        />
                    </Grid>

                    {requiresMoves && (
                        <>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth size="small" sx={fieldStyles}>
                                    <InputLabel>Moneda</InputLabel>
                                    <Controller
                                        control={control}
                                        name="currency"
                                        defaultValue="COP"
                                        render={({ field }) => (
                                            <Select {...field} label="Moneda">
                                                <MenuItem value="COP">COP</MenuItem>
                                                <MenuItem value="USD">USD</MenuItem>
                                            </Select>
                                        )}
                                    />
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth size="small"
                                    label="Cantidad de jugadas"
                                    value={moves ?? ''}
                                    onChange={(e) => handleMovesChange(e.target.value)}
                                    sx={fieldStyles}
                                    {...numberFieldProps({ min: 0 })}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth size="small"
                                    label={`Valor (${moneyFormat(Number(amount || 0))})`}
                                    value={amount ?? ''}
                                    onChange={(e) => handleAmountChange(e.target.value)}
                                    sx={fieldStyles}
                                    {...numberFieldProps({ min: 0, step: PRICE_PER_MOVE })}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Alert sx={alertStyles}>
                                    1 jugada = {moneyFormat(PRICE_PER_MOVE)} COP
                                </Alert>
                            </Grid>
                        </>
                    )}
                </Grid>
            </SectionCard>

            {requiresMoves && (
                <SectionCard>
                    <SectionTitle>
                        <CardGiftcardOutlinedIcon />
                        <Typography>Jugadas de regalo</Typography>
                    </SectionTitle>

                    <TextField
                        fullWidth size="small"
                        label="Jugadas de regalo"
                        value={gift ?? ''}
                        onChange={(e) => {
                            const v = e.target.value;
                            setValue('gift_moves', v === '' ? null : parseInt(v));
                        }}
                        // error={giftExceeds}
                        // helperText={giftExceeds ? `Máximo ${giftMax}` : `Hasta ${giftMax}`}
                        sx={fieldStyles}
                    // {...numberFieldProps({ min: 0, max: giftMax })}
                    />
                </SectionCard>
            )}

            {requiresMoves && (
                <SectionCard>
                    <SectionTitle>
                        <PaidOutlinedIcon />
                        <Typography>Pago</Typography>
                    </SectionTitle>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small" sx={fieldStyles}>
                                <InputLabel>Forma de pago</InputLabel>
                                <Controller
                                    control={control}
                                    name="income_types_id"
                                    render={({ field }) => (
                                        <Select {...field} label="Forma de pago">
                                            {incomeTypes.map((it) => (
                                                <MenuItem key={it.id} value={it.id}>
                                                    {it.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                />
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth size="small"
                                label="Días para pagar"
                                defaultValue={1}
                                {...register('days_to_pay')}
                                sx={fieldStyles}
                                {...numberFieldProps({ min: 1 })}
                            />
                        </Grid>
                    </Grid>
                </SectionCard>
            )}

            <SectionCard>
                <SectionTitle>
                    <TimerOutlinedIcon />
                    <Typography>Condiciones legales</Typography>
                </SectionTitle>

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth size="small"
                            label="Días incumplimiento"
                            defaultValue={1}
                            {...register('days_breach')}
                            sx={fieldStyles}
                            {...numberFieldProps({ min: 1 })}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth size="small"
                            label="Días rescisión"
                            defaultValue={1}
                            {...register('days_rescission')}
                            sx={fieldStyles}
                            {...numberFieldProps({ min: 1 })}
                        />
                    </Grid>
                </Grid>
            </SectionCard>

        </Wrapper>
    );
};

export default Step2Commercial;

const Wrapper = styled(Box)`
    width: 100%;
`;

const Header = styled.div`
    margin-bottom: 1rem;

    .title {
        color: #10285f;
        font-size: 1.2rem;
        font-weight: 900;
    }

    .subtitle {
        color: rgba(15, 34, 77, 0.6);
        font-size: 0.9rem;
        margin-top: 0.2rem;
    }
`;

const SectionCard = styled.div`
    padding: 1rem;
    margin-bottom: 1rem;
    border-radius: 18px;
    border: 1px solid rgba(15, 34, 77, 0.1);
    background: rgba(16, 40, 95, 0.025);
`;

const SectionTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 0.6rem;
    margin-bottom: 0.8rem;

    svg {
        color: #d6aa3a;
    }

    p {
        font-weight: 900;
        color: #10285f;
    }
`;

const alertStyles = {
    borderRadius: '12px',
    background: 'rgba(214,170,58,0.15)',
    color: '#10285f',
    border: '1px solid rgba(214,170,58,0.3)',
};